/**
 * Attaching a dataset, planning what to do with it, and doing it.
 *
 * The lifecycle is four separate moments, and keeping them separate is the
 * point:
 *
 *   1. ATTACH   the zip is read in the browser; nothing leaves the machine.
 *   2. PLAN     a plan is computed from the archive's structure and the text.
 *   3. CONFIRM  the plan is rendered as a card. Still nothing has been created.
 *   4. RUN      only now does anything reach the server.
 *
 * A language model can influence step 2 and nothing else, and even there its
 * output passes through `coercePlan` before it is shown, let alone executed.
 *
 * Bytes live in `datasetVault` (a module Map), never in the store and never on
 * a message — every message is JSON-stringified into localStorage, where a File
 * becomes `{}`. After a reload the vault is empty while the card is still on
 * screen, which is a state the card renders explicitly rather than offering a
 * Run button that cannot work.
 */
import { useCallback, useRef } from 'react';
import { useI18n } from '../../../i18n';
import { getLoggedInOwnerId } from '../../../utils/auth';
import datasetApi, { listProjects, refinePlan } from '../api/datasetApi';
import useAssistantStore from '../store/useAssistantStore';
import {
  MAX_IMPORT_IMAGES,
  analyzeEntries,
  formatBytes,
  scanZip,
} from '../utils/datasetScan';
import {
  buildDetectionItems,
  readAnnotationMeta,
  reconcileClassNames,
  withClassIds,
} from '../utils/datasetAnnotations';
import {
  answerQuestion,
  createInterview,
  currentQuestion,
  interviewMirror,
  parseAnswer,
  planFromInterview,
  reopenQuestion,
  selectImportable,
  trainingGate,
} from '../utils/datasetInterview';
import { autoBoxImages, summariseBoxes } from '../utils/datasetAutoBox';
import { fromFiles, fromZip, nameOfSelection, pathOf, toFileList } from '../utils/datasetSource';
import { readImageSize } from '../utils/imageDimensions';
import { buildBasePlan, coercePlan, describePlan, describeRun } from '../utils/datasetPlan';
import { assignClasses, reconcileClasses, runPlan } from '../utils/datasetPlanRunner';
import { downscaleImage } from '../utils/imageDownscale';
import { dropDataset, getDataset, hasDataset, putDataset } from '../utils/datasetVault';
import { newId } from '../utils/threadStorage';

/**
 * Cap on the archive itself.
 *
 * JSZip holds the whole file plus the entries it decodes in memory, so a very
 * large upload takes the tab down rather than reporting anything useful. The
 * message names the number instead of failing mysteriously.
 */
export const MAX_ZIP_BYTES = 250 * 1024 * 1024;

const isZip = (file) =>
  /\.zip$/i.test(file?.name || '') ||
  ['application/zip', 'application/x-zip-compressed', 'multipart/x-zip'].includes(file?.type);

const useDatasetBuild = () => {
  const { t, lang } = useI18n();
  const ownerId = getLoggedInOwnerId();

  const activeThreadId = useAssistantStore((state) => state.activeThreadId);
  const attachments = useAssistantStore((state) => state.attachments);
  const runs = useAssistantStore((state) => state.runs);

  const {
    appendMessage,
    clearAttachment,
    ensureThread,
    patchBuildCard,
    patchRun,
    setAttachment,
    setDraft,
    setError,
    startRun,
  } = useAssistantStore.getState();

  /** One AbortController per running card. */
  const controllersRef = useRef(new Map());
  /** Detections kept per card, so a cancel does not throw away GPU time. */
  const boxCacheRef = useRef(new Map());
  /**
   * The interview's clustering, per card: image path -> class id.
   *
   * Kept for the same reason as boxCacheRef — the user watched it happen and
   * approved the groups it produced, so the import must create THOSE groups.
   * `/cluster/auto` picks its own k when none was given, so re-running it can
   * legitimately return a different number of groups.
   */
  const clusterCacheRef = useRef(new Map());
  /** The user's projects, so the confirm card can name an existing one. */
  const projectsRef = useRef([]);

  const attachment = activeThreadId ? attachments[activeThreadId] || null : null;

  // ---- 1. attach -----------------------------------------------------------

  /**
   * Accept a zip OR a dropped folder.
   *
   * The reported failure began "after giving my folder" — dropping a directory
   * is at least as natural as zipping it first, and every browser hands the
   * same thing over: a flat File[] where each file remembers its path.
   */
  const attachMany = useCallback(
    async (files) => {
      // toFileList, not Array.from: both call sites pass a single File, which
      // Array.from turns into [] — see the note on toFileList. That silently
      // killed every attach.
      const list = toFileList(files);
      const threadId = ensureThread();
      if (!list.length) return;

      const single = list.length === 1 ? list[0] : null;
      if (!single || !isZip(single)) {
        const images = list.filter((file) => /\.(jpe?g|png|webp)$/i.test(pathOf(file)));
        if (!images.length) {
          setAttachment(threadId, {
            id: newId(),
            fileName: nameOfSelection(list),
            status: 'error',
            error: t('assistant.attach.empty'),
          });
          return;
        }
        const id = newId();
        const folderName = nameOfSelection(list);
        // Phase by phase, so a large folder shows movement rather than a
        // spinner that could equally mean "working" or "wedged".
        setAttachment(threadId, {
          id, fileName: folderName, status: 'reading', phase: 'scanning', count: list.length,
        });
        try {
          const source = fromFiles(list);
          const analysis = analyzeEntries(source.entries());
          setAttachment(threadId, {
            id, fileName: folderName, status: 'reading', phase: 'labels',
            count: analysis.totalImages,
          });
          const meta = await readAnnotationMeta(analysis, (path) => source.readText(path));
          putDataset(id, { source, analysis, meta, fileName: folderName });
          setAttachment(threadId, {
            id,
            fileName: folderName,
            status: 'ready',
            images: analysis.totalImages,
            bytes: analysis.totalBytes,
            folders: analysis.groups.length,
            layout: analysis.layout,
            skipped: analysis.skipped.junk + analysis.skipped.nonImage,
            groupNames: analysis.groups.slice(0, 6).map((group) => group.name),
            annotationFormat: meta.format || null,
            classNames: (meta.names || []).slice(0, 8),
            classCount: (meta.names || []).length,
            unsupportedFormat: analysis.annotation.format === 'voc' ? 'voc' : null,
          });
        } catch (error) {
          setAttachment(threadId, {
            id, fileName: folderName, status: 'error', error: t('assistant.attach.failed'),
          });
        }
        return;
      }

      await attachZip(single, threadId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ensureThread, setAttachment, t],
  );

  const attachZip = useCallback(
    async (file, knownThreadId) => {
      const threadId = knownThreadId || ensureThread();
      if (!file) return;

      if (!isZip(file)) {
        setAttachment(threadId, {
          id: newId(), fileName: file.name, status: 'error', error: t('assistant.attach.notZip'),
        });
        return;
      }
      if (file.size > MAX_ZIP_BYTES) {
        setAttachment(threadId, {
          id: newId(),
          fileName: file.name,
          status: 'error',
          error: t('assistant.attach.tooLarge', {
            size: formatBytes(file.size), limit: formatBytes(MAX_ZIP_BYTES),
          }),
        });
        return;
      }

      const id = newId();
      setAttachment(threadId, {
        id, fileName: file.name, status: 'reading', phase: 'unpacking', bytes: file.size,
      });

      try {
        // Imported here rather than at module scope so ~100KB of zip code is
        // not in the assistant's initial bundle for every user who never
        // attaches anything.
        const { default: JSZip } = await import('jszip');
        const { zip, analysis } = await scanZip(file, JSZip);

        if (!analysis.totalImages) {
          setAttachment(threadId, {
            id, fileName: file.name, status: 'error', error: t('assistant.attach.empty'),
          });
          return;
        }

        setAttachment(threadId, {
          id, fileName: file.name, status: 'reading', phase: 'labels',
          count: analysis.totalImages, bytes: file.size,
        });

        // Read the handful of tiny metadata files an annotated archive carries,
        // so the card can show the dataset's REAL class names before the user
        // commits to anything. No image is read here.
        const source = fromZip(zip);
        const meta = await readAnnotationMeta(analysis, (path) => source.readText(path));

        putDataset(id, { source, analysis, meta, fileName: file.name });
        // Only a small, serialisable SUMMARY reaches the store — the analysis
        // holds one row per image and would be megabytes for a large archive.
        setAttachment(threadId, {
          id,
          fileName: file.name,
          status: 'ready',
          images: analysis.totalImages,
          bytes: analysis.totalBytes,
          folders: analysis.groups.length,
          layout: analysis.layout,
          skipped: analysis.skipped.junk + analysis.skipped.nonImage,
          groupNames: analysis.groups.slice(0, 6).map((group) => group.name),
          // Detection metadata, when the archive turned out to carry boxes.
          annotationFormat: meta.format || null,
          classNames: (meta.names || []).slice(0, 8),
          classCount: (meta.names || []).length,
          unsupportedFormat:
            analysis.annotation.format === 'voc' ? 'voc' : null,
        });
      } catch (error) {
        setAttachment(threadId, {
          id, fileName: file.name, status: 'error', error: t('assistant.attach.failed'),
        });
      }
    },
    [ensureThread, setAttachment, t],
  );

  const remove = useCallback(() => {
    if (!activeThreadId) return;
    const staged = attachments[activeThreadId];
    if (staged) dropDataset(staged.id);
    clearAttachment(activeThreadId);
  }, [activeThreadId, attachments, clearAttachment]);

  // ---- 2/3. plan and card --------------------------------------------------

  /** Would this message start a build? Used to branch the composer's submit. */
  const planFor = useCallback(
    (text) => {
      if (!attachment || attachment.status !== 'ready') return null;
      const held = getDataset(attachment.id);
      if (!held) return null;
      return buildBasePlan(held.analysis, text, {
        fileName: attachment.fileName,
        meta: held.meta,
      });
    },
    [attachment],
  );


  // =========================================================================
  // The interview
  //
  // This is the path that replaces "go and do it in Project Management". With a
  // dataset attached, ANY message that is not a plain question opens an
  // interview card — the assistant then asks only what it does not already know
  // and does the rest itself.
  // =========================================================================

  /** Patch an interview card's state, deep-merging the interview object. */
  const patchInterview = useCallback(
    (threadId, messageId, patch) => {
      const thread = useAssistantStore.getState().threads.find((entry) => entry.id === threadId);
      const message = thread && thread.messages.find((entry) => entry.id === messageId);
      if (!message) return null;
      const next = { ...(message.interview || {}), ...patch };
      patchBuildCard(threadId, messageId, {
        interview: next,
        content: interviewMirror(next, t),
      });
      return next;
    },
    [patchBuildCard, t],
  );

  /** Open an interview for the staged dataset. */
  const startInterview = useCallback(
    (text) => {
      const threadId = ensureThread();
      if (!attachment || attachment.status !== 'ready') return false;
      const held = getDataset(attachment.id);
      if (!held) return false;

      setError(null);
      appendMessage(threadId, {
        role: 'user',
        content: (text || '').trim() || t('assistant.attach.hint'),
        attachmentName: attachment.fileName,
      });

      const state = createInterview({
        scan: held.analysis,
        meta: held.meta,
        instruction: text,
        fileName: attachment.fileName,
        projects: projectsRef.current,
      });

      const messageId = appendMessage(threadId, {
        role: 'card',
        kind: 'interview',
        content: interviewMirror(state, t),
        interview: state,
        attachmentId: attachment.id,
        attachmentName: attachment.fileName,
      });

      setDraft(threadId, '');
      clearAttachment(threadId);

      // A fresh project list, so the confirm card can say "adding to your
      // existing project X" rather than surprising the user with a reuse.
      listProjects(ownerId)
        .then((projects) => {
          projectsRef.current = projects;
          patchInterview(threadId, messageId, { projects });
        })
        .catch(() => patchInterview(threadId, messageId, { projectsUnreadable: true }));

      // Nothing left to ask? Go straight on to the work, or to the confirm.
      if (!currentQuestion(state)) {
        if (state.stage === 'work') runWork(threadId, messageId, { ...state, attachmentId: attachment.id });
        else patchInterview(threadId, messageId, { stage: state.stage });
      }
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachment, ensureThread, ownerId, patchInterview, t],
  );

  /** Answer a question. */
  const answer = useCallback(
    (message, questionId, value, extra) => {
      if (!activeThreadId) return;
      const state = message.interview;
      if (questionId === 'review') {
        patchInterview(activeThreadId, message.id, { stage: 'confirm' });
        return;
      }
      const next = answerQuestion(state, questionId, value, extra || {});
      const stored = patchInterview(activeThreadId, message.id, next);
      if (stored && stored.stage === 'work') {
        runWork(activeThreadId, message.id, { ...stored, attachmentId: message.attachmentId });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeThreadId, patchInterview],
  );

  /** A typed answer, parsed against THIS question only. */
  const answerTyped = useCallback(
    (message, text) => {
      const state = message.interview;
      const question = currentQuestion(state);
      if (!question) return false;
      const parsed = parseAnswer(question, text);
      if (!parsed.ok) {
        // Re-ask once, quoting what was read — never a modal, never a third try.
        patchInterview(activeThreadId, message.id, {
          error: t('assistant.interview.notUnderstood', { text: String(text).slice(0, 60) }),
        });
        return true;
      }
      answer(message, question.id, parsed.value, parsed);
      return true;
    },
    [activeThreadId, answer, patchInterview, t],
  );

  /**
   * Go back and change one answer.
   *
   * ABORTS whatever is in flight first. Without that, pressing Change during
   * the work stage left the run going: it finished a moment later and patched
   * `stage: 'review'` straight over the question the user had just reopened, so
   * the answer they changed was silently discarded and the old result stood.
   *
   * The caches are keyed by IMAGE PATH, not by the answer that produced them,
   * so a changed answer must drop them or the next run returns the previous
   * one's output instantly. That is why "Try different words" appeared to do
   * nothing: every path was already cached, so `pending` was empty and the
   * identical boxes came straight back.
   */
  const reopen = useCallback(
    (message, questionId) => {
      if (!activeThreadId) return;
      const controller = controllersRef.current.get(message.id);
      if (controller) controller.abort();
      controllersRef.current.delete(message.id);
      if (questionId === 'objects') boxCacheRef.current.delete(message.id);
      if (questionId === 'grouping' || questionId === 'task') {
        clusterCacheRef.current.delete(message.id);
        boxCacheRef.current.delete(message.id);
      }
      patchRun(message.id, { percent: 0 });
      patchInterview(activeThreadId, message.id, reopenQuestion(message.interview, questionId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeThreadId, patchInterview],
  );

  /**
   * The expensive step: cluster, or box every image.
   *
   * Runs BEFORE anything is created, so a failure or a cancel leaves the
   * account exactly as it was.
   */
  const runWork = useCallback(
    async (threadId, messageId, state, heldOverride) => {
      const held = heldOverride || getDataset(state.attachmentId);
      if (!held) {
        patchInterview(threadId, messageId, { stage: 'ask', error: t('assistant.attach.expired') });
        return;
      }
      const controller = new AbortController();
      controllersRef.current.set(messageId, controller);
      patchInterview(threadId, messageId, { stage: 'work' });

      const selection = selectImportable(held.analysis.images || []);
      const plan = planFromInterview(state);

      const notes = [];
      const note = (key, params) => notes.push({ key, params });

      try {
        if (state.answers.task === 'detection') {
          const cache = boxCacheRef.current.get(messageId) || new Map();
          boxCacheRef.current.set(messageId, cache);
          const out = await autoBoxImages({
            items: selection.images,
            classes: plan.classes,
            api: datasetApi,
            readBlob: (path) => held.source.readBlob(path),
            cache,
            signal: controller.signal,
            onProgress: (progress) => patchRun(messageId, progress),
          });
          const stats = summariseBoxes(out.results, plan.classes, 0);
          patchInterview(threadId, messageId, {
            stage: 'review',
            result: {
              ...stats,
              classes: plan.classes,
              cancelled: out.cancelled,
              /*
               * How many images the detector could not be asked about at all.
               *
               * autoBoxImages caches a FAILED image as `boxes: []`, which is
               * indistinguishable from "nothing matched" once it reaches
               * summariseBoxes. So a detection endpoint that is down, or a GPU
               * that OOMs, produced a review card reading "that found very
               * little — those words may not match what is in the images", and
               * the user changed perfectly good words in response to a server
               * failure.
               */
              failed: out.failed || 0,
            },
          });
        } else if (plan.grouping.mode === 'cluster') {
          /*
           * Group the images HERE, while the user is watching, rather than
           * after they press Create.
           *
           * Before this, the work stage for a classification import was a
           * no-op: it patched `stage: 'work'` and `stage: 'review'` in the same
           * synchronous tick, so the spinner could not paint for a single frame
           * and the review card claimed "Here is what I found" over an EMPTY
           * chip row — `planFromInterview` builds no class names for auto/few/
           * many. The clustering then ran after the point of no return, where a
           * 25s wait (measured: 300 images) has no Stop and nothing to show.
           *
           * Doing it here puts the expensive step before the irreversible one,
           * where cancelling is free and the result can actually be reviewed.
           */
          patchRun(messageId, { percent: 0 });
          const assigned = await assignClasses({
            plan,
            items: selection.images,
            readBlob: (path) => held.source.readBlob(path),
            downscale: downscaleImage,
            api: datasetApi,
            signal: controller.signal,
            note,
            onProgress: (percent) => patchRun(messageId, { percent }),
          });
          const classes = reconcileClasses(plan, assigned);
          const perClass = classes.map(
            (entry) => assigned.filter((item) => item.classId === entry.id).length,
          );
          clusterCacheRef.current.set(
            messageId,
            new Map(assigned.map((item) => [item.path, item.classId])),
          );
          patchInterview(threadId, messageId, {
            stage: 'review',
            result: {
              classes,
              perClass,
              images: selection.images.length,
              grouped: perClass.reduce((sum, n) => sum + n, 0),
            },
            notes,
          });
        } else {
          // folders / annotations / unlabeled — the archive already decided.
          patchInterview(threadId, messageId, {
            stage: 'review',
            result: { classes: plan.classes, images: selection.images.length },
          });
        }
      } catch (error) {
        // A cancel is the user's own decision, not a failure to report as one.
        const cancelled =
          (error && (error.name === 'AbortError' || error.name === 'CanceledError')) ||
          controller.signal.aborted;
        patchInterview(threadId, messageId, {
          stage: 'ask',
          error: cancelled ? null : (error && error.message) || t('assistant.error.generic'),
        });
      } finally {
        controllersRef.current.delete(messageId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patchInterview, patchRun, t],
  );

  /** The one irreversible moment. */
  const confirm = useCallback(
    async (message, names) => {
      if (!activeThreadId) return;
      const state = message.interview;
      const plan = planFromInterview(state, names);
      patchInterview(activeThreadId, message.id, { stage: 'running' });
      const outcome = await execute(activeThreadId, message.id, plan, message.attachmentId, {
        boxCache: boxCacheRef.current.get(message.id),
        // The groups the user just approved on the review card. Re-clustering
        // could return a different k, and then the classes created would not be
        // the ones they said yes to.
        clusterAssignment: clusterCacheRef.current.get(message.id),
      });
      patchInterview(activeThreadId, message.id, {
        stage: 'done',
        outcome,
        // How many images actually got a box, measured on the review card.
        // Without it a detection import that found nothing still offers to
        // train on it.
        ...trainingGate(outcome, plan, t, {
          labeledImages: state.result ? state.result.imagesWithBoxes : null,
        }),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeThreadId, patchInterview, t],
  );

  const queueTrainingFor = useCallback(
    async (message) => {
      const state = message.interview;
      if (!state.outcome || !state.outcome.versionId || state.trainBlockedKey) return;
      try {
        await datasetApi.queueTraining({
          versionId: state.outcome.versionId,
          modelType: state.outcome.modelType,
          ownerId,
        });
        patchInterview(activeThreadId, message.id, { trainQueued: true });
      } catch (error) {
        patchInterview(activeThreadId, message.id, {
          error: (error && error.message) || t('assistant.error.generic'),
        });
      }
    },
    [activeThreadId, ownerId, patchInterview, t],
  );

  // ---- 4. run --------------------------------------------------------------

  const execute = useCallback(
    async (threadId, messageId, plan, attachmentId, options = {}) => {
      const held = getDataset(attachmentId);
      if (!held) {
        const failure = { status: 'failed', error: t('assistant.attach.expired') };
        patchBuildCard(threadId, messageId, { run: failure });
        return failure;
      }
      if (!ownerId) {
        const failure = { status: 'failed', error: t('assistant.build.error.signedOut') };
        patchBuildCard(threadId, messageId, { run: failure });
        return failure;
      }

      const controller = new AbortController();
      controllersRef.current.set(messageId, controller);
      // Notes gathered while preparing the run, merged with the runner's own.
      const note = [];

      startRun(messageId, { steps: {}, percent: 0 });
      patchBuildCard(threadId, messageId, { run: { status: 'running', error: null } });

      /*
       * The cap is applied here, not in the scan: the user sees the full count
       * on the card and the note explains what is being left out.
       *
       * `selectImportable`, NOT `slice(0, MAX_IMPORT_IMAGES)`. The two pick
       * DIFFERENT IMAGES — selectImportable takes an even spread across the
       * whole set and then drops by total bytes, while slice takes the first N
       * — and every other part of this feature already uses the former: the
       * card's "I can import N of them", the interview's clustering, and the
       * detection branch below. With slice here, a 500-image folder was
       * grouped from one set and imported from another, so the approved
       * grouping would not have covered the images that actually arrived.
       * It also restores the byte cap on this path, which is the thing that
       * keeps one un-chunkable multipart request inside the proxy's limits.
       */
      let items = selectImportable(held.analysis.images || []).images;
      let planToRun = plan;

      if (plan.target.modelType === 'bounding_box' && options.boxCache) {
        /*
         * The interview already ran the vocabulary over every image and cached
         * the result. Re-detecting here would repay for hundreds of GPU forward
         * passes the user has already waited through.
         */
        const selection = selectImportable(held.analysis.images || []);
        items = selection.images.map((image) => {
          const cached = options.boxCache.get(image.path) || {};
          return {
            path: image.path,
            name: image.name,
            annotations: (cached.boxes || []).map((box) => ({
              classId: plan.classes[box.classIndex] ? plan.classes[box.classIndex].id : null,
              centerX: box.centerX,
              centerY: box.centerY,
              width: box.width,
              height: box.height,
            })).filter((box) => box.classId != null),
            originalWidth: (cached.size && cached.size.width) || 0,
            originalHeight: (cached.size && cached.size.height) || 0,
          };
        });
        const boxed = items.filter((item) => item.annotations.length).length;
        note.push({ codeKey: 'boxesFound', params: {
          boxes: items.reduce((sum, item) => sum + item.annotations.length, 0), images: boxed,
        } });
      } else if (plan.target.modelType === 'bounding_box') {
        /*
         * Read the annotations. This is the one pass that touches image bytes
         * before the upload, and only for YOLO — a COCO document states every
         * image's size itself, so nothing is read for it.
         */
        const built = await buildDetectionItems({
          analysis: held.analysis,
          meta: held.meta,
          limit: MAX_IMPORT_IMAGES,
          readText: (path) => held.source.readText(path),
          readBytes: (path) => held.source.readBytes(path),
          readSize: readImageSize,
        });

        // The names file can disagree with the labels. Inventing the missing
        // names beats dropping the boxes that use them.
        const { names, invented } = reconcileClassNames(
          plan.classes.map((entry) => entry.name),
          built.stats.highestClassIndex,
        );
        const classes = names.map((name, index) => ({
          id: index + 1, name, count: null, source: String(index),
        }));
        if (invented.length) note.push({ codeKey: 'inventedClasses', params: { count: invented.length } });
        if (built.stats.segmentFiles) {
          note.push({ codeKey: 'segmentFiles', params: { count: built.stats.segmentFiles } });
        }
        if (built.stats.missingSize) {
          note.push({ codeKey: 'missingSize', params: { count: built.stats.missingSize } });
        }
        note.push({ codeKey: 'boxesFound', params: { boxes: built.stats.boxes, images: built.stats.withBoxes } });

        planToRun = { ...plan, classes };
        items = withClassIds(built.items, classes);
      }

      const result = await runPlan({
        plan: planToRun,
        preassigned: options.clusterAssignment,
        items,
        readBlob: (path) => held.source.readBlob(path),
        downscale: downscaleImage,
        ownerId,
        api: datasetApi,
        signal: controller.signal,
        // Step transitions go to the card; per-image percentage goes to the
        // non-persisted `runs` slice, so progress never churns localStorage.
        onStep: (id, state, detail) =>
          patchRun(messageId, {
            steps: { [id]: state },
            percent: detail && detail.percent != null ? detail.percent : undefined,
            // How many rows have actually landed, during `verify` — the step
            // that otherwise polls for up to 30s showing nothing at all.
            counted: detail && detail.counted != null ? detail.counted : undefined,
            current: id,
          }),
      });

      controllersRef.current.delete(messageId);

      patchBuildCard(threadId, messageId, {
        run: {
          status: result.status,
          error: result.error,
          projectId: result.projectId,
          projectName: result.projectName,
          modelId: result.modelId,
          modelName: result.modelName,
          modelDoc: result.modelDoc,
          versionId: result.versionId,
          imported: result.imported,
          expected: result.expected,
          notes: [...note, ...result.notes],
        },
        plan: result.classes && result.classes.length ? { classes: result.classes } : undefined,
        content: describeRun(planToRun, result, t),
      });

      return {
        status: result.status,
        error: result.error,
        projectId: result.projectId,
        projectName: result.projectName,
        modelId: result.modelId,
        modelName: result.modelName,
        modelDoc: result.modelDoc,
        modelType: plan.target.modelType,
        versionId: result.versionId,
        imported: result.imported,
        expected: result.expected,
      };
    },
    [ownerId, patchBuildCard, patchRun, startRun, t],
  );

  /** Turn a staged dataset plus an instruction into a card, then run nothing. */
  const startBuild = useCallback(
    (text) => {
      const threadId = ensureThread();
      const plan = planFor(text);
      if (!plan || !attachment) return false;

      setError(null);
      appendMessage(threadId, {
        role: 'user',
        // Never blank: a message with empty content is dropped from the model's
        // history, unsearchable in the sidebar, and titles the thread "".
        content: (text || '').trim() || t('assistant.attach.hint'),
        attachmentName: attachment.fileName,
      });

      const cardId = appendMessage(threadId, {
        role: 'card',
        kind: 'build',
        content: describePlan(plan, t),
        plan,
        attachmentId: attachment.id,
        attachmentName: attachment.fileName,
        run: null,
      });

      setDraft(threadId, '');
      // The dataset stays in the vault — the card holds its id — but it is no
      // longer "staged", so the composer returns to a plain text box.
      clearAttachment(threadId);

      /*
       * Ask the server to improve the NAMES, after the card is already on
       * screen. Deliberately not awaited and deliberately unguarded: the card
       * is complete and runnable right now, and a refinement that is slow,
       * absent or broken must cost the user nothing. Whatever comes back goes
       * through `coercePlan` against the plan we just built, so the server can
       * change wording and nothing else.
       */
      const held = getDataset(attachment.id);
      refinePlan({
        instruction: text,
        lang,
        ownerId,
        plan,
        manifest: {
          fileName: attachment.fileName,
          layout: attachment.layout,
          totalImages: attachment.images,
          folders: (held ? held.analysis.groups : []).map((group) => ({
            name: group.name, count: group.count,
          })),
          sampleNames: (held ? held.analysis.images : []).slice(0, 12).map((image) => image.name),
        },
      }).then((result) => {
        if (!result || !result.plan) return;
        const refined = coercePlan(result.plan, plan);
        patchBuildCard(threadId, cardId, {
          plan: refined,
          content: describePlan(refined, t),
          engine: result.engine,
        });
      });

      return true;
    },
    [appendMessage, attachment, clearAttachment, ensureThread, lang, ownerId, patchBuildCard, planFor, setDraft, setError, t],
  );

  const run = useCallback(
    (message) => {
      if (!activeThreadId || !message) return;
      execute(activeThreadId, message.id, message.plan, message.attachmentId);
    },
    [activeThreadId, execute],
  );

  const cancel = useCallback((messageId) => {
    const controller = controllersRef.current.get(messageId);
    if (controller) controller.abort();
  }, []);

  /** Rename the classes on a card that has not run yet. */
  const renameClasses = useCallback(
    (message, names) => {
      if (!activeThreadId || !message || !message.plan) return;
      const classes = message.plan.classes.map((entry, index) => ({
        ...entry,
        name: names[index] != null ? names[index] : entry.name,
      }));
      const plan = { ...message.plan, classes };
      patchBuildCard(activeThreadId, message.id, {
        plan: { classes },
        content: describePlan(plan, t),
      });
    },
    [activeThreadId, patchBuildCard, t],
  );

  /** The newest interview card still expecting an answer, or null. */
  const activeInterview = (() => {
    const thread = useAssistantStore
      .getState()
      .threads.find((entry) => entry.id === activeThreadId);
    if (!thread) return null;
    for (let i = thread.messages.length - 1; i >= 0; i -= 1) {
      const message = thread.messages[i];
      if (message.kind === 'interview' && message.interview && message.interview.stage === 'ask') {
        return message;
      }
    }
    return null;
  })();

  return {
    attachment,
    attach: attachMany,
    remove,
    planFor,
    // The interview — the path that replaced deflecting to Project Management.
    startInterview,
    answer,
    answerTyped,
    reopen,
    confirm,
    queueTrainingFor,
    activeInterview,
    // The one-shot plan card, still used when everything was already stated.
    startBuild,
    run,
    cancel,
    renameClasses,
    runs,
    isHeld: (id) => hasDataset(id),
  };
};

export default useDatasetBuild;
